import React, { useState, useEffect, useCallback } from "react";
import { useSession } from "hooks/useSession";
import "./PlanInfo.css";

import { websitePlanService } from "service/websitePlanService";
import { dataBackupPlanService } from "service/dataBackupPlanService";
import { otherChargeService } from "service/otherChargeService";

const BACKUP_PERIODS = ["30_days", "60_days", "90_days", "180_days", "365_days", "720_days"];

const emptyPlanForm = {
  id: null,
  plan_name: "",
  duration: "",
  one_time_cost: "",
  sleep_time_charges: "",
  daily_increment: "",
  server_status: "true",
  status: "true",
  created_by: "Super Admin",
};

const emptyBackupForm = {
  id: null,
  website_plan: "",
  monthly_business_limit: "",
  backup_attributes: {
    "30_days": { enabled: true, price: 0 },
    "60_days": { enabled: true, price: 0 },
    "90_days": { enabled: true, price: 0 },
    "180_days": { enabled: false, price: 0 },
    "365_days": { enabled: true, price: 0 },
    "720_days": { enabled: false, price: 0 },
    ad1: 0,
    ad2: 0,
  },
  status: "true",
  created_by: { id: 1, name: "Super Admin", role: "SUPER_ADMIN" },
};

const emptyChargeForm = {
  id: null,
  website_plan: "",
  service_name: "",
  domain_renewal: "false",
  ssl: "false",
  quantity: { default: 1, min: 1, max: 10 },
  unit: { type: "Year" },
  amount: { price: 0, gst: 18 },
  day_applicable: "false",
  quantity_applicable: "true",
  status: "true",
  created_by: { id: 1, name: "Super Admin", role: "SUPER_ADMIN" },
};

const ADMIN_TABS = [
  { key: "plans", label: "Website Plans" },
  { key: "backup", label: "Backup Pricing" },
  { key: "charges", label: "Other Charges" },
];

const PlanInfo = () => {
  const { role } = useSession();
  const isAdmin = role === "Main Admin";

  // ----- viewer state -----
  const [websitePlans, setWebsitePlans] = useState([]);
  const [backupPlans, setBackupPlans] = useState([]);
  const [otherCharges, setOtherCharges] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  // ----- admin state -----
  const [adminTab, setAdminTab] = useState("plans");
  const [planForm, setPlanForm] = useState(emptyPlanForm);
  const [backupForm, setBackupForm] = useState(emptyBackupForm);
  const [chargeForm, setChargeForm] = useState(emptyChargeForm);
  const [saving, setSaving] = useState(false);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      const [plansRes, backupRes, chargesRes] = await Promise.all([
        websitePlanService.getAll(),
        dataBackupPlanService.getAll(),
        otherChargeService.getAll(),
      ]);

      const plansList = Array.isArray(plansRes?.data) ? plansRes.data : [];

      const rawBackupList = Array.isArray(backupRes?.data) ? backupRes.data : [];
      const backupList = rawBackupList.map((item) =>
        typeof dataBackupPlanService.normalize === "function"
          ? dataBackupPlanService.normalize(item)
          : item
      );

      const rawChargesList = Array.isArray(chargesRes?.data) ? chargesRes.data : [];
      const chargesList = rawChargesList.map((item) =>
        typeof otherChargeService.normalize === "function"
          ? otherChargeService.normalize(item)
          : item
      );

      setWebsitePlans(plansList);
      setBackupPlans(backupList);
      setOtherCharges(chargesList);

      setSelectedPlanId((prev) => {
        if (prev && plansList.some((p) => String(p.id) === String(prev))) return prev;
        return plansList[0]?.id ?? null;
      });
    } catch (err) {
      console.error("Failed to load plan data", err);
      setApiError(err.message || "Failed to load plan data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const backupRowsForSelectedPlan = backupPlans.filter(
    (b) => String(b.website_plan) === String(selectedPlanId)
  );
  const selectedPlan = websitePlans.find((p) => String(p.id) === String(selectedPlanId));

  // ---------------- Website Plan CRUD ----------------
  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setApiError(null);
    try {
      await websitePlanService.addOrUpdate(planForm);
      setPlanForm(emptyPlanForm);
      await loadAllData();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditPlan = async (id) => {
    try {
      const res = await websitePlanService.getById(id);
      const d = Array.isArray(res?.data) ? res.data[0] : res?.data || res;
      if (d) setPlanForm({ ...emptyPlanForm, ...d, id: d.id });
      setAdminTab("plans");
    } catch (err) {
      setApiError(err.message);
    }
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm("Delete this website plan?")) return;
    try {
      await websitePlanService.remove(id);
      await loadAllData();
    } catch (err) {
      setApiError(err.message);
    }
  };

  // ---------------- Backup Plan CRUD ----------------
  const updateBackupAttr = (period, field, value) => {
    setBackupForm((prev) => ({
      ...prev,
      backup_attributes: {
        ...prev.backup_attributes,
        [period]: { ...prev.backup_attributes[period], [field]: value },
      },
    }));
  };

  const handleBackupSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setApiError(null);
    try {
      await dataBackupPlanService.addOrUpdate(backupForm);
      setBackupForm(emptyBackupForm);
      await loadAllData();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditBackup = async (id) => {
    try {
      const res = await dataBackupPlanService.getById(id);
      const raw = Array.isArray(res?.data) ? res.data[0] : res?.data || res;
      if (raw) setBackupForm({ ...emptyBackupForm, ...dataBackupPlanService.normalize(raw), id: raw.id });
      setAdminTab("backup");
    } catch (err) {
      setApiError(err.message);
    }
  };

  const handleDeleteBackup = async (id) => {
    if (!window.confirm("Delete this backup plan?")) return;
    try {
      await dataBackupPlanService.remove(id);
      await loadAllData();
    } catch (err) {
      setApiError(err.message);
    }
  };

  // ---------------- Other Charge CRUD ----------------
  const handleChargeSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setApiError(null);
    try {
      await otherChargeService.addOrUpdate(chargeForm);
      setChargeForm(emptyChargeForm);
      await loadAllData();
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditCharge = async (id) => {
    try {
      const res = await otherChargeService.getById(id);
      const raw = Array.isArray(res?.data) ? res.data[0] : res?.data || res;
      if (raw) setChargeForm({ ...emptyChargeForm, ...otherChargeService.normalize(raw), id: raw.id });
      setAdminTab("charges");
    } catch (err) {
      setApiError(err.message);
    }
  };

  const handleDeleteCharge = async (id) => {
    if (!window.confirm("Delete this charge?")) return;
    try {
      await otherChargeService.remove(id);
      await loadAllData();
    } catch (err) {
      setApiError(err.message);
    }
  };

  const isLive = (v) => v === "true" || v === true;

  return (
    <div className="pi-page">
      {/* Header */}
      <div className="pi-header">
        <div>
          <span className="pi-eyebrow">Infrastructure · Pricing Console</span>
          <h1 className="pi-title">Website Plan Console</h1>
        </div>
        <div className="pi-header-right">
          {loading && <span className="pi-spinner" aria-label="Loading" />}
          {loading ? "Syncing…" : `${websitePlans.length} plan${websitePlans.length === 1 ? "" : "s"} configured`}
        </div>
      </div>

      {apiError && (
        <div className="pi-alert pi-alert-error" role="alert">
          {apiError}
        </div>
      )}

      {/* Plan rail */}
      {websitePlans.length === 0 && !loading ? (
        <div className="pi-plan-rail-empty">
          No website plans yet{isAdmin ? " — add one below to get started." : "."}
        </div>
      ) : (
        <div className="pi-plan-rail">
          {websitePlans.map((p, idx) => (
            <button
              key={p.id}
              className="pi-plan-chip"
              data-active={String(selectedPlanId) === String(p.id)}
              onClick={() => setSelectedPlanId(p.id)}
              type="button"
            >
              <span className="pi-plan-chip-id">P{String(idx + 1).padStart(2, "0")}</span>
              <span className="pi-plan-chip-name">{p.plan_name}</span>
              <span className="pi-plan-chip-price pi-mono">₹{p.one_time_cost}</span>
            </button>
          ))}
        </div>
      )}

      {/* Stat cards */}
      {selectedPlan && (
        <div className="pi-stats">
          <div className="pi-stat-card">
            <div className="pi-stat-label">Registration Fee</div>
            <div className="pi-stat-value pi-mono">
              ₹{selectedPlan.one_time_cost || 0} <span style={{ fontSize: 12, color: "var(--pi-ink-soft)" }}>+ GST</span>
            </div>
          </div>
          <div className="pi-stat-card">
            <div className="pi-stat-label">Business Duration Limit</div>
            <div className="pi-stat-value">{selectedPlan.duration || "—"}</div>
          </div>
          <div className="pi-stat-card">
            <div className="pi-stat-label">Server Connection</div>
            <div className="pi-status-row">
              <span className="pi-live-dot" />
              <span className="pi-stat-value">
                {isLive(selectedPlan.server_status) ? "Live" : "Offline"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Backup pricing table */}
      <div className="pi-card">
        <div className="pi-card-header">
          <h2>Data Backup Pricing</h2>
          <span className="pi-card-header-sub">{selectedPlan ? selectedPlan.plan_name : "No plan selected"}</span>
        </div>
        <div className="pi-table-wrap">
          <table className="pi-table">
            <thead>
              <tr>
                <th>Monthly Business Limit</th>
                <th>30 Days</th>
                <th>60 Days</th>
                <th>90 Days</th>
                <th>180 Days</th>
                <th>365 Days</th>
                <th>720 Days</th>
                {isAdmin && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {backupRowsForSelectedPlan.length === 0 && (
                <tr className="pi-empty-row">
                  <td colSpan={isAdmin ? 8 : 7}>
                    No backup pricing configured for this plan yet.
                  </td>
                </tr>
              )}
              {backupRowsForSelectedPlan.map((row) => {
                const attrs = row.backup_attributes || {};
                return (
                  <tr key={row.id}>
                    <td className="pi-num">{row.monthly_business_limit}</td>
                    {BACKUP_PERIODS.map((period) => (
                      <td key={period} className="pi-num">
                        {attrs[period]?.enabled ? (
                          `₹${attrs[period].price}`
                        ) : (
                          <span className="pi-num-off">—</span>
                        )}
                      </td>
                    ))}
                    {isAdmin && (
                      <td>
                        <div className="pi-row-actions">
                          <button className="pi-icon-btn" onClick={() => handleEditBackup(row.id)}>Edit</button>
                          <button className="pi-icon-btn pi-icon-btn-danger" onClick={() => handleDeleteBackup(row.id)}>Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer notes */}
      <div className="pi-notes">
        <span>Sleep-time charges apply up to ₹499</span>
        <span>Data is backed up twice daily</span>
        <span>GST is charged extra on all server plans listed above</span>
      </div>

      {/* ================= ADMIN PANEL ================= */}
      {isAdmin && (
        <div className="pi-admin">
          <div className="pi-admin-eyebrow">Admin · Manage Console Data</div>
          <div className="pi-admin-tabs">
            {ADMIN_TABS.map((t) => (
              <button
                key={t.key}
                className="pi-admin-tab"
                data-active={adminTab === t.key}
                onClick={() => setAdminTab(t.key)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* --- Website Plans --- */}
          {adminTab === "plans" && (
            <div className="pi-card">
              <form onSubmit={handlePlanSubmit} className="pi-form">
                <div className="pi-field" style={{ gridColumn: "span 3" }}>
                  <label>Plan name</label>
                  <input className="pi-input" value={planForm.plan_name}
                    onChange={(e) => setPlanForm({ ...planForm, plan_name: e.target.value })}
                    placeholder="e.g. Premium Hosting" required />
                </div>
                <div className="pi-field" style={{ gridColumn: "span 2" }}>
                  <label>Duration</label>
                  <input className="pi-input" value={planForm.duration}
                    onChange={(e) => setPlanForm({ ...planForm, duration: e.target.value })}
                    placeholder="12 Months" required />
                </div>
                <div className="pi-field" style={{ gridColumn: "span 2" }}>
                  <label>One-time cost</label>
                  <input className="pi-input" type="number" value={planForm.one_time_cost}
                    onChange={(e) => setPlanForm({ ...planForm, one_time_cost: e.target.value })}
                    placeholder="2500" required />
                </div>
                <div className="pi-field" style={{ gridColumn: "span 2" }}>
                  <label>Sleep-time charge</label>
                  <input className="pi-input" type="number" value={planForm.sleep_time_charges}
                    onChange={(e) => setPlanForm({ ...planForm, sleep_time_charges: e.target.value })}
                    placeholder="100" />
                </div>
                <div className="pi-field" style={{ gridColumn: "span 2" }}>
                  <label>Daily increment</label>
                  <input className="pi-input" type="number" value={planForm.daily_increment}
                    onChange={(e) => setPlanForm({ ...planForm, daily_increment: e.target.value })}
                    placeholder="10" />
                </div>
                <div className="pi-field" style={{ gridColumn: "span 1" }}>
                  <button type="submit" className="pi-btn pi-btn-primary" disabled={saving}>
                    {saving ? "…" : planForm.id ? "Update" : "Add"}
                  </button>
                </div>
              </form>

              <div className="pi-table-wrap">
                <table className="pi-table">
                  <thead>
                    <tr><th>Plan</th><th>Duration</th><th>Cost</th><th>Status</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {websitePlans.length === 0 && (
                      <tr className="pi-empty-row"><td colSpan={5}>No plans yet — add one above.</td></tr>
                    )}
                    {websitePlans.map((p) => (
                      <tr key={p.id}>
                        <td>{p.plan_name}</td>
                        <td>{p.duration}</td>
                        <td className="pi-num">₹{p.one_time_cost}</td>
                        <td>
                          <span className={`pi-badge ${isLive(p.status) ? "pi-badge-on" : "pi-badge-off"}`}>
                            {isLive(p.status) ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="pi-row-actions">
                            <button className="pi-icon-btn" onClick={() => handleEditPlan(p.id)}>Edit</button>
                            <button className="pi-icon-btn pi-icon-btn-danger" onClick={() => handleDeletePlan(p.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* --- Backup Pricing --- */}
          {adminTab === "backup" && (
            <div className="pi-card">
              <form onSubmit={handleBackupSubmit}>
                <div className="pi-form" style={{ borderBottom: "none" }}>
                  <div className="pi-field" style={{ gridColumn: "span 5" }}>
                    <label>Website plan</label>
                    <select className="pi-select" value={backupForm.website_plan}
                      onChange={(e) => setBackupForm({ ...backupForm, website_plan: e.target.value })} required>
                      <option value="">Select a plan</option>
                      {websitePlans.map((p) => (
                        <option key={p.id} value={p.id}>{p.plan_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="pi-field" style={{ gridColumn: "span 7" }}>
                    <label>Monthly business limit</label>
                    <input className="pi-input" value={backupForm.monthly_business_limit}
                      onChange={(e) => setBackupForm({ ...backupForm, monthly_business_limit: e.target.value })}
                      placeholder="e.g. 1500000" required />
                  </div>
                </div>

                <div className="pi-attr-grid">
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--pi-ink-soft)", textTransform: "uppercase" }}>Period</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--pi-ink-soft)", textTransform: "uppercase" }}>On</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--pi-ink-soft)", textTransform: "uppercase" }}>Price (₹)</div>

                  {BACKUP_PERIODS.map((period) => {
                    const attr = backupForm.backup_attributes[period];
                    return (
                      <div className="pi-attr-row" key={period}>
                        <div className="pi-attr-period">{period.replace("_", " ")}</div>
                        <div>
                          <input type="checkbox" className="pi-toggle" checked={attr.enabled}
                            onChange={(e) => updateBackupAttr(period, "enabled", e.target.checked)} />
                        </div>
                        <div>
                          <input type="number" className="pi-input" value={attr.price} disabled={!attr.enabled}
                            onChange={(e) => updateBackupAttr(period, "price", Number(e.target.value))} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ padding: "8px 18px 18px" }}>
                  <button type="submit" className="pi-btn pi-btn-primary" disabled={saving}>
                    {saving ? "Saving…" : backupForm.id ? "Update backup plan" : "Add backup plan"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* --- Other Charges --- */}
          {adminTab === "charges" && (
            <div className="pi-card">
              <form onSubmit={handleChargeSubmit} className="pi-form">
                <div className="pi-field" style={{ gridColumn: "span 3" }}>
                  <label>Website plan</label>
                  <select className="pi-select" value={chargeForm.website_plan}
                    onChange={(e) => setChargeForm({ ...chargeForm, website_plan: e.target.value })} required>
                    <option value="">Select a plan</option>
                    {websitePlans.map((p) => (
                      <option key={p.id} value={p.id}>{p.plan_name}</option>
                    ))}
                  </select>
                </div>
                <div className="pi-field" style={{ gridColumn: "span 3" }}>
                  <label>Service name</label>
                  <input className="pi-input" value={chargeForm.service_name}
                    onChange={(e) => setChargeForm({ ...chargeForm, service_name: e.target.value })}
                    placeholder="Domain Renewal" required />
                </div>
                <div className="pi-field" style={{ gridColumn: "span 2" }}>
                  <label>Price</label>
                  <input className="pi-input" type="number" value={chargeForm.amount.price}
                    onChange={(e) => setChargeForm({ ...chargeForm, amount: { ...chargeForm.amount, price: Number(e.target.value) } })} />
                </div>
                <div className="pi-field" style={{ gridColumn: "span 2" }}>
                  <label>GST %</label>
                  <input className="pi-input" type="number" value={chargeForm.amount.gst}
                    onChange={(e) => setChargeForm({ ...chargeForm, amount: { ...chargeForm.amount, gst: Number(e.target.value) } })} />
                </div>
                <div className="pi-field" style={{ gridColumn: "span 2" }}>
                  <button type="submit" className="pi-btn pi-btn-primary" disabled={saving}>
                    {saving ? "…" : chargeForm.id ? "Update" : "Add"}
                  </button>
                </div>
              </form>

              <div className="pi-table-wrap">
                <table className="pi-table">
                  <thead>
                    <tr><th>Service</th><th>Plan</th><th>Price</th><th>GST</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {otherCharges.length === 0 && (
                      <tr className="pi-empty-row"><td colSpan={5}>No charges yet — add one above.</td></tr>
                    )}
                    {otherCharges.map((c) => {
                      const plan = websitePlans.find((p) => String(p.id) === String(c.website_plan));
                      return (
                        <tr key={c.id}>
                          <td>{c.service_name}</td>
                          <td>{plan?.plan_name || c.website_plan}</td>
                          <td className="pi-num">₹{c.amount?.price}</td>
                          <td className="pi-num">{c.amount?.gst}%</td>
                          <td>
                            <div className="pi-row-actions">
                              <button className="pi-icon-btn" onClick={() => handleEditCharge(c.id)}>Edit</button>
                              <button className="pi-icon-btn pi-icon-btn-danger" onClick={() => handleDeleteCharge(c.id)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanInfo;